import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { catchError, Observable, switchMap, throwError } from 'rxjs';
import { AuthenticationService } from '@app/feature/authentication/service/authentication.service';
import { Router } from '@angular/router';

/**
 * Intercepteur pour la gestion automatique du refresh token
 * Si le token access est expiré, tente automatiquement de le rafraîchir
 */
export const jwtRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthenticationService);
  const router = inject(Router);
  const platformId = inject(PLATFORM_ID);

  // Les opérations publiques d'authentification ne doivent jamais déclencher une restauration
  // d'une ancienne session. Les endpoints authentifiés sous /auth, notamment /auth/profile,
  // restent en revanche éligibles au refresh.
  if (isPublicAuthenticationRequest(req.url)) {
    return next(req);
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Si erreur 401 et qu'on n'est pas déjà en train de rafraîchir
      if (error.status === 401 && !req.url.includes('/refresh')) {
        // Un visiteur anonyme (aucune session en cours) ne doit pas être redirigé vers la page
        // de connexion : un simple 401 sur un widget secondaire d'une page publique (bandeau
        // promotionnel, offres...) rendait sinon toute la page inaccessible.
        if (!authService.hasSessionHint()) {
          return throwError(() => error);
        }

        return handleTokenRefresh(req, next, authService, router, platformId);
      }

      return throwError(() => error);
    }),
  );
};

function isPublicAuthenticationRequest(url: string): boolean {
  return [
    '/auth/login',
    '/auth/logout',
    '/auth/register',
    '/auth/otp/',
    '/auth/forgot',
    '/auth/reset-password',
    '/auth/refresh',
    '/auth/email/',
    '/auth/firebase',
  ].some((path) => url.includes(path));
}

/**
 * Tente de rafraîchir le token et de rejouer la requête
 */
function handleTokenRefresh(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthenticationService,
  router: Router,
  platformId: object,
): Observable<HttpEvent<unknown>> {
  // Appel au backend pour rafraîchir le token (utilise le cookie automatiquement)
  return authService.refreshToken().pipe(
    switchMap((response) => {
      // Cloner la requête avec le nouveau token
      const clonedReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${response.accessToken}`,
        },
      });

      // Rejouer la requête
      return next(clonedReq);
    }),
    catchError((refreshError) => {
      // Échec du refresh, nettoyage et redirection
      cleanupAndRedirect(authService, router, platformId);
      return throwError(() => refreshError);
    }),
  );
}

/**
 * Nettoie le localStorage et redirige vers login
 */
function cleanupAndRedirect(
  authService: AuthenticationService,
  router: Router,
  platformId: object,
): void {
  authService.clearLocalSession();
  if (!isPlatformBrowser(platformId)) return;
  router.navigate(['/authentication/login'], {
    queryParams: { returnUrl: router.url, reason: 'session_expired' },
  });
}
